/**
 * 루트 컴포넌트
 *
 * 라우트:
 *   /             → 랜딩 (Home)
 *   /terms        → 이용약관 (Terms)
 *   /privacy      → 개인정보처리방침 (Privacy)
 *   /admin/login  → 어드민 로그인
 *   /admin/*      → 어드민 (인증 필요)
 *   *             → 404 (NotFound)
 *
 * 참고: firebase.json 의 모든 비-API 경로는 /index.html 로 리라이트 → React Router가 처리
 *
 * ──────────────────────────────────────────────────────────────────
 * [2026-09-02] 어드민 코드 분할 (C11)
 *
 * 문제:
 *   어드민 페이지 5개를 정적 import 하고 있어서 랜딩 방문자가 어드민
 *   코드를 통째로 받았다. 더 큰 문제는 연쇄 효과였다 —
 *   AdminHome/AdminInquiries/AdminAnalytics/useInquiryCounts 가
 *   'firebase/firestore' 를 정적 import 하는 탓에 manualChunks 의
 *   firebase-vendor(668KB)가 초기 로딩 그래프에 끌려 들어왔다.
 *
 *   실측(2026-09-02, 배포본):
 *     index 170KB + react-vendor 164KB + firebase-vendor 668KB
 *     + form-vendor 79KB + motion-vendor 1KB = 1,084KB
 *     ↑ firebase-vendor 는 index.html 의 modulepreload 로 즉시 전송됨
 *
 * 해결:
 *   어드민 진입점(RequireAdmin/AdminLayout 포함)을 전부 React.lazy 로
 *   내린다. 정적 import 체인이 끊기면서 firebase-vendor 도 함께
 *   지연 로드로 바뀐다. 어드민 사용자는 /admin 진입 시 한 번만 받는다.
 *
 * 주의:
 *   - AuthProvider 는 lazy 대상이 아니다. 라우트 밖에서 쓰이고,
 *     내부적으로 firebase/auth 를 동적 import 하므로 이미 안전하다.
 *   - AdminLogin 도 lazy 로 내린다. 랜딩 방문자에겐 불필요한 코드다.
 * ──────────────────────────────────────────────────────────────────
 */
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastProvider } from '@/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import {
  AuthProvider,
  registerTokenGetter,
  useAuth,
} from '@/contexts/AuthContext';
import { Home } from '@/pages/Home';
import { Terms } from '@/pages/Terms';
import { Privacy } from '@/pages/Privacy';
import { NotFound } from '@/pages/NotFound';

// ── 어드민 전용 — 지연 로드 ────────────────────────────────
// named export 이므로 default 로 매핑해준다.
const RequireAdmin = lazy(() =>
  import('@/components/admin/RequireAdmin').then((m) => ({
    default: m.RequireAdmin,
  }))
);
const AdminLayout = lazy(() =>
  import('@/components/admin/AdminLayout').then((m) => ({
    default: m.AdminLayout,
  }))
);
const AdminLogin = lazy(() =>
  import('@/pages/admin/AdminLogin').then((m) => ({ default: m.AdminLogin }))
);
const AdminHome = lazy(() =>
  import('@/pages/admin/AdminHome').then((m) => ({ default: m.AdminHome }))
);
const AdminInquiries = lazy(() =>
  import('@/pages/admin/AdminInquiries').then((m) => ({
    default: m.AdminInquiries,
  }))
);
const AdminAnalytics = lazy(() =>
  import('@/pages/admin/AdminAnalytics').then((m) => ({
    default: m.AdminAnalytics,
  }))
);
const AdminMaintenance = lazy(() =>
  import('@/pages/admin/AdminMaintenance').then((m) => ({
    default: m.AdminMaintenance,
  }))
);

function TokenRegistrar() {
  const { getIdToken } = useAuth();
  useEffect(() => {
    registerTokenGetter(getIdToken);
  }, [getIdToken]);
  return null;
}

/**
 * 어드민 청크 로딩 중 표시.
 * 어드민은 사내 전용이고 대개 캐시되므로 최소한으로만 둔다.
 */
function AdminFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-w">
      <div className="flex flex-col items-center gap-3">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-faint border-t-gold" />
        <p className="text-xs text-mute">불러오는 중…</p>
      </div>
    </div>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <TokenRegistrar />
        <BrowserRouter>
          {/*
            Suspense 를 Routes 바깥에 두면 랜딩 라우트까지 감싸지만,
            랜딩 컴포넌트는 정적 import 라 절대 suspend 되지 않는다.
            어드민 라우트 진입 시에만 fallback 이 보인다.
          */}
          <Suspense fallback={<AdminFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                }
              >
                <Route index element={<AdminHome />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="maintenance" element={<AdminMaintenance />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <ToastContainer />
      </AuthProvider>
    </ToastProvider>
  );
}
